import React, { useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CalendorIcon from "../../assets/icons/calendor-icon";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Calendar header for every DatePicker: ‹ › month arrows plus month and year
// dropdowns, so a date months/years away is two clicks instead of many.
export const CalendarHeader = ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}) => {
  const thisYear = new Date().getFullYear();
  const years = [];
  for (let y = thisYear - 15; y <= thisYear + 10; y++) years.push(y);
  if (!years.includes(date.getFullYear())) years.push(date.getFullYear());
  years.sort((a, b) => a - b);

  const arrow =
    "w-8 h-8 shrink-0 flex items-center justify-center rounded-full text-[var(--heading-color)] hover:bg-[var(--row-hover-color)] hover:text-[var(--primary)] disabled:opacity-30 disabled:hover:bg-transparent";
  const select =
    "h-8 pl-2 pr-1 rounded-lg border border-[var(--light-border-color)] bg-white text-[13px] fw_600 text-[var(--heading-color)] cursor-pointer hover:border-[var(--primary)] focus:outline-none focus:border-[var(--primary)]";

  return (
    <div className="flex items-center justify-between gap-1 px-1">
      <button
        type="button"
        aria-label="Previous month"
        className={arrow}
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
      >
        ‹
      </button>
      <div className="flex items-center gap-1.5">
        <select
          aria-label="Month"
          className={select}
          value={date.getMonth()}
          onChange={(e) => changeMonth(Number(e.target.value))}
        >
          {MONTHS.map((m, i) => (
            <option key={m} value={i}>
              {m}
            </option>
          ))}
        </select>
        <select
          aria-label="Year"
          className={select}
          value={date.getFullYear()}
          onChange={(e) => changeYear(Number(e.target.value))}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        aria-label="Next month"
        className={arrow}
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
      >
        ›
      </button>
    </div>
  );
};

// Shared calendar setup - spread onto any <DatePicker> so every screen gets
// the same header, Today button and readable display format
// ("03 Nov 2026"). The value the screens receive is unchanged.
export const calendarUiProps = {
  renderCustomHeader: (props) => <CalendarHeader {...props} />,
  todayButton: "Today",
  showPopperArrow: false,
  popperPlacement: "bottom-start",
  dateFormat: "dd MMM yyyy",
};

const DatePickerInput = ({
  placeholder = "",
  onChange = () => {},
  value,
  className = "w-full bg-transparent focus:outline-none text-[var(--head-input-fg-color)] text-[length:var(--head-input-font-size)] font-[var(--head-input-font-weight)] placeholder:text-[var(--head-input-placeholder-color)] placeholder:text-[length:var(--head-input-font-size)] placeholder:font-[var(--head-input-place-font-weight)]",
  conClass = "",
  width = "150px",
  height = "32px",
  borderClass = "border-[var(--head-input-border-color)]",
  maxDate = null,
  minDate = null,
  isDisabled = false,
  // Renders the calendar popup into a portal under document.body instead of
  // inline — needed when this sits inside an ancestor with overflow:hidden
  // (e.g. an animated collapsible panel), which would otherwise clip it.
  portalId,
  // "left" keeps every existing caller's layout unchanged; pass "right" to
  // place the icon after the input instead.
  iconPosition = "left",
  // For a From / To pair: "start" on From (rangeEnd = the To value), "end"
  // on To (rangeStart = the From value). The calendar then shades the range
  // between them. Leave unset for a standalone date.
  rangeRole,
  rangeStart,
  rangeEnd,
}) => {
  const datePickerRef = useRef(null);
  const toDate = (v) => (v ? new Date(v) : null);
  const selected = toDate(value);

  const rangeProps =
    rangeRole === "start"
      ? { selectsStart: true, startDate: selected, endDate: toDate(rangeEnd) }
      : rangeRole === "end"
      ? { selectsEnd: true, startDate: toDate(rangeStart), endDate: selected }
      : {};

  // Quick picks under the calendar (Today / Yesterday), kept within min/max.
  // Compared as yyyy-MM-dd strings so time of day never blocks "Today".
  const ymd = (v) => (v ? toDate(v).toLocaleDateString("sv-SE") : "");
  const quickPick = (daysAgo) => {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    const day = ymd(d);
    if ((minDate && day < ymd(minDate)) || (maxDate && day > ymd(maxDate)))
      return;
    onChange(day);
    datePickerRef.current?.setOpen(false);
  };
  const quickBtn =
    "px-3 py-1 rounded-full text-xs fw_600 text-[var(--primary)] hover:bg-[var(--row-hover-color)]";

  const icon = (
    <button
      type="button"
      tabIndex={-1}
      disabled={isDisabled}
      onClick={() => datePickerRef.current?.setOpen(true)}
      className={isDisabled ? "cursor-not-allowed" : "cursor-pointer"}
    >
      <CalendorIcon width="14px" height="14px" />
    </button>
  );

  return (
    <div
      style={{
        height,
        width,
      }}
      className={`flex items-center border ${borderClass} rounded-md gap-[8px] px-2 z-20 ${isDisabled ? "bg-gray-100 cursor-not-allowed" : ""} ${conClass}`}
    >
      {iconPosition === "left" && icon}
      <DatePicker
        ref={datePickerRef}
        {...calendarUiProps}
        // Footer below is Today + Yesterday in one row instead.
        todayButton={undefined}
        {...rangeProps}
        wrapperClassName="flex-1 min-w-0"
        {...(portalId ? { portalId, popperClassName: "!z-[9999]" } : {})}
        // Only when this field has a date - the library also shows the clear
        // button whenever a range end (startDate/endDate) is set, which put a
        // dead "x" on an empty From Date once a To Date was picked.
        isClearable={!isDisabled && !!selected}
        disabled={isDisabled}
        selected={selected} // Ensure value is a Date object
        onChange={
          (date) => onChange(date ? date.toLocaleDateString("sv-SE") : "")
          // 👈 Return empty string instead of null
        }
        placeholderText={placeholder}
        onKeyDown={(e) => e.preventDefault()} // 👈 Prevent manual typing
        className={`${className} ${isDisabled ? "cursor-not-allowed" : ""}`}
        maxDate={toDate(maxDate)} // 👈 Add maxDate prop
        minDate={toDate(minDate)} // 👈 Add minDate prop
      >
        <div className="flex items-center justify-center gap-2 py-2 border-t border-[var(--light-border-color)]">
          <button type="button" className={quickBtn} onClick={() => quickPick(0)}>
            Today
          </button>
          <span className="text-[var(--light-border-color)]">|</span>
          <button type="button" className={quickBtn} onClick={() => quickPick(1)}>
            Yesterday
          </button>
        </div>
      </DatePicker>
      {iconPosition === "right" && icon}
    </div>
  );
};

export default DatePickerInput;
